import { useState, useEffect, JSX } from 'react'
import { useLocation, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import styles from './nav.module.scss'
import logo from '../../../../assets/img/logo/vision.jpg'

const Nav = (): JSX.Element => {
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false)
  const [scrolled, setScrolled] = useState<boolean>(false)
  const toggleDrawer = (): void => setIsDrawerOpen((prev) => !prev)

  const { pathname } = useLocation()

  useEffect(() => {
    if (window.innerWidth <= 1024) setIsDrawerOpen(false)
  }, [pathname])

  useEffect(() => {
    document.body.style.overflow = isDrawerOpen ? 'hidden' : 'auto'
    return () => { document.body.style.overflow = 'auto' }
  }, [isDrawerOpen])

  useEffect(() => {
    const handleResize = () => { if (window.innerWidth > 1024) setIsDrawerOpen(false) }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const navLinks = [
    { to: '/', label: 'Home' },
    { to: '/services', label: 'Services' },
    { to: '/about-us', label: 'About' },
    { to: '/team', label: 'Team' },
    { to: '/contact-us', label: 'Contact' },
  ]

  return (
    <nav className={`${styles.wrapper} ${scrolled ? styles.wrapperScrolled : ''}`}>
      <div className={styles.bar}>
        <Link to='/' className={styles.logoLink}>
          <img src={logo} alt='Vision International' className={styles.logo} />
        </Link>

        <span className={styles.spacer} />

        <ul className={styles.links}>
          {navLinks.map((link) => (
            <li key={link.to}>
              <Link to={link.to} className={pathname === link.to ? styles.activeLink : ''}>
                {link.label}
              </Link>
            </li>
          ))}
          <li>
            <a href='https://onitsoft.com/sokar-logistics' target='_blank' rel='noopener noreferrer' className={styles.tmsLink}>
              TMS
            </a>
          </li>
          <li>
            <Link to='/contact-us' className={styles.ctaLink}>Get a Quote</Link>
          </li>
        </ul>

        <div
          className={`${styles.burgerMenu} ${isDrawerOpen && styles.burgerMenuActive}`}
          onClick={toggleDrawer}
        >
          <span /><span /><span />
        </div>
      </div>

      <motion.div
        className={styles.drawer}
        initial={{ x: '100%' }}
        animate={{ x: isDrawerOpen ? 0 : '100%' }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
      >
        <div className={styles.drawerLogo}>
          <img src={logo} alt='Vision International' className={styles.drawerLogoImg} />
        </div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: isDrawerOpen ? 1 : 0 }}
          transition={{ duration: 0.2, delay: 0.2 }}
        >
          {navLinks.map((link) => (
            <Link
              key={link.to}
              className={styles.mobileLink}
              to={link.to}
              onClick={() => setIsDrawerOpen(false)}
            >
              {link.label}
            </Link>
          ))}
          <a
            href='https://onitsoft.com/sokar-logistics'
            target='_blank'
            rel='noopener noreferrer'
            className={`${styles.mobileLink} ${styles.mobileLinkTms}`}
            onClick={() => setIsDrawerOpen(false)}
          >
            TMS
          </a>
          <Link
            className={`${styles.mobileLink} ${styles.mobileLinkCta}`}
            to='/contact-us'
            onClick={() => setIsDrawerOpen(false)}
          >
            Get a Quote
          </Link>
        </motion.div>
      </motion.div>

      {isDrawerOpen && (
        <motion.div
          className={styles.mobileOverlay}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={() => setIsDrawerOpen(false)}
        />
      )}
    </nav>
  )
}

export default Nav
